import type { IsoDate } from './dates';

/**
 * Етапи конкурсу — вільний список (назва, колір, порядок), свій у кожному конкурсі.
 * Автоматика прив'язана не до назви, а до «дії» етапу: її можна призначити будь-якому етапу.
 */
export const STAGE_KINDS = ['winner', 'offer_sent', 'hired', 'rejected', 'exit'] as const;
export type StageKind = (typeof STAGE_KINDS)[number];

/** Дії, які завершують участь без працевлаштування (окремий блок у списку етапів). */
export const EXIT_KINDS = ['rejected', 'exit'] as const satisfies readonly StageKind[];

export const STAGE_COLORS = [
  'slate',
  'sky',
  'indigo',
  'violet',
  'cyan',
  'fuchsia',
  'amber',
  'lime',
  'emerald',
  'rose',
  'orange',
  'zinc',
] as const;
export type StageColor = (typeof STAGE_COLORS)[number];

export interface StageDef {
  id: string;
  name: string;
  kind: StageKind | null;
  color: StageColor;
  position: number;
}

export type StageTemplate = Omit<StageDef, 'id' | 'position'>;

/** Стандартний набір, з якого почали; далі його можна змінити в довідниках або з конкурсу. */
export const DEFAULT_STAGES: readonly StageTemplate[] = [
  { name: 'Новий', kind: null, color: 'slate' },
  { name: 'Телефонна розмова', kind: null, color: 'sky' },
  { name: 'Запрошено на співбесіду', kind: null, color: 'indigo' },
  { name: 'Співбесіду проведено', kind: null, color: 'violet' },
  { name: 'Переможець', kind: 'winner', color: 'amber' },
  { name: 'Лист переможцю', kind: 'offer_sent', color: 'lime' },
  { name: 'Вийшов на роботу', kind: 'hired', color: 'emerald' },
  { name: 'Відмова', kind: 'rejected', color: 'rose' },
  { name: 'Знявся з конкурсу', kind: 'exit', color: 'zinc' },
  { name: 'Відмовився від роботи', kind: 'exit', color: 'orange' },
];

/** Рівні воронки рекрутингу: однакові для конкурсів з різними етапами (за діями етапів). */
export const FUNNEL_LEVELS = ['applied', 'progressed', 'winner', 'offer', 'hired'] as const;
export type FunnelLevel = (typeof FUNNEL_LEVELS)[number];

export function isExitStage(stage: Pick<StageDef, 'kind'>): boolean {
  return stage.kind === 'rejected' || stage.kind === 'exit';
}

/** Чи дійшов кандидат до перемоги (переможець, лист, вихід на роботу). */
export function isWinnerStage(stage: Pick<StageDef, 'kind'>): boolean {
  return stage.kind === 'winner' || stage.kind === 'offer_sent' || stage.kind === 'hired';
}

/** Порядок показу: спершу основний шлях, потім етапи вибуття — кожна група за позицією. */
export function orderStages<T extends Pick<StageDef, 'kind' | 'position'>>(
  stages: readonly T[],
): T[] {
  return [...stages].sort(
    (a, b) => Number(isExitStage(a)) - Number(isExitStage(b)) || a.position - b.position,
  );
}

/** Етап, на який потрапляє новий кандидат: перший в основному шляху. */
export function entryStage<T extends Pick<StageDef, 'kind' | 'position'>>(
  stages: readonly T[],
): T | undefined {
  return orderStages(stages).find((s) => !isExitStage(s));
}

/**
 * Перевірка набору етапів перед збереженням: непорожні унікальні назви, хоча б один етап
 * основного шляху (туди потрапляють нові кандидати). Повертає текст помилки або null.
 */
export function validateStages(stages: readonly Pick<StageDef, 'name' | 'kind'>[]): string | null {
  if (!stages.some((s) => !isExitStage(s))) {
    return 'Потрібен хоча б один етап основного шляху — туди потрапляють нові кандидати';
  }
  const seen = new Set<string>();
  for (const s of stages) {
    const key = s.name.trim().toLowerCase();
    if (!key) return 'Назва етапу не може бути порожньою';
    if (seen.has(key)) return `Етап «${s.name.trim()}» повторюється`;
    seen.add(key);
  }
  return null;
}

export type TransitionKind = 'forward' | 'backward' | 'exit';

export type TransitionError =
  'same_stage' | 'comment_required' | 'started_at_required' | 'contract_ref_required';

type StageRef = Pick<StageDef, 'id' | 'kind' | 'position'>;

export interface TransitionInput {
  from: StageRef;
  to: StageRef;
  comment?: string | null | undefined;
  startedAt?: IsoDate | null | undefined;
  contractRef?: string | null | undefined;
  /** Дати, які вже стоять у заявці (щоб не перезаписувати відредаговані вручну). */
  winnerLetterSentAt?: IsoDate | null | undefined;
  rejectionLetterSentAt?: IsoDate | null | undefined;
}

export interface TransitionEffects {
  winnerLetterSentAt?: IsoDate;
  rejectionLetterSentAt?: IsoDate;
  startedAt?: IsoDate;
  contractRef?: string;
}

export type TransitionResult =
  | { ok: true; kind: TransitionKind; effects: TransitionEffects }
  | { ok: false; error: TransitionError };

/** Повернення назад: на раніший етап, з вибуття в основний шлях або з «вийшов на роботу». */
function isBackward(from: StageRef, to: StageRef): boolean {
  if (isExitStage(to)) return from.kind === 'hired';
  if (isExitStage(from) || from.kind === 'hired') return true;
  return to.position < from.position;
}

/**
 * Правило 5.3 для вільного списку етапів. Переходити можна між будь-якими етапами конкурсу;
 * повернення назад потребує коментаря. Дії етапу дають побічні ефекти:
 * - «лист переможцю» — дата листа переможцю (сьогодні, якщо ще не стоїть);
 * - «вихід на роботу» — обов'язкові дата виходу і наказ / договір;
 * - «відмова» — дата листа з відмовою.
 */
export function checkTransition(input: TransitionInput, today: IsoDate): TransitionResult {
  const { from, to } = input;
  if (from.id === to.id) return { ok: false, error: 'same_stage' };

  const kind: TransitionKind = isBackward(from, to)
    ? 'backward'
    : isExitStage(to)
      ? 'exit'
      : 'forward';
  if (kind === 'backward' && !input.comment?.trim()) {
    return { ok: false, error: 'comment_required' };
  }

  const effects: TransitionEffects = {};
  if (to.kind === 'offer_sent') effects.winnerLetterSentAt = input.winnerLetterSentAt ?? today;
  if (to.kind === 'rejected') effects.rejectionLetterSentAt = input.rejectionLetterSentAt ?? today;
  if (to.kind === 'hired') {
    const startedAt = input.startedAt ?? null;
    const contractRef = input.contractRef?.trim() ?? '';
    if (!startedAt) return { ok: false, error: 'started_at_required' };
    if (!contractRef) return { ok: false, error: 'contract_ref_required' };
    effects.startedAt = startedAt;
    effects.contractRef = contractRef;
  }
  return { ok: true, kind, effects };
}

/** Чи вимагає перехід коментаря (повернення назад). */
export function requiresComment(from: StageRef, to: StageRef): boolean {
  return from.id !== to.id && isBackward(from, to);
}
